/**
 * User Love Slide
 * Sign-up traction + user quotes centering around "I need this"
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
  BigNumber,
  BigNumberLabel,
  DarkAccentBox,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Work-in-Progress overlay                           */
/* -------------------------------------------------- */

const WipOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const WipBanner = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize['2xl']}, 4vw, ${typography.fontSize['4xl']});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.highlight.extraDark};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(6px);
  padding: ${spacing.lg} ${spacing['3xl']};
  border: 2px solid ${colors.highlight.extraLight};
  border-radius: ${borderRadius.xl};
  transform: rotate(-8deg);
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

/* -------------------------------------------------- */
/* Local styled components                            */
/* -------------------------------------------------- */

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
`;

const SignupContext = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  color: ${colors.highlight.light};
  margin: 0;
  text-align: center;
  font-style: italic;
`;

const QuoteGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg};
  width: 100%;
  max-width: 960px;

  ${media.md} {
    grid-template-columns: 1fr 1fr;
  }
`;

const QuoteCard = styled(DarkAccentBox)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const QuoteText = styled.blockquote`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  font-weight: ${typography.fontWeights.light};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: ${typography.lineHeight.relaxed};
  font-style: italic;

  &::before {
    content: '\\201C';
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.brand.primary};
    line-height: 0;
    vertical-align: -0.3em;
    margin-right: 2px;
  }

  &::after {
    content: '\\201D';
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.brand.primary};
    line-height: 0;
    vertical-align: -0.3em;
    margin-left: 2px;
  }
`;

const QuoteAttribution = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.xs};
  color: ${colors.brand.primaryLight};
  text-align: right;
`;

const HighlightSpan = styled.span`
  color: ${colors.brand.primary};
  font-weight: ${typography.fontWeights.medium};
  font-style: normal;
`;

const BottomNote = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1vw, ${typography.fontSize.sm});
  color: ${colors.highlight.light};
  margin: 0;
  text-align: center;
  max-width: 640px;
`;

/* -------------------------------------------------- */
/* Data                                               */
/* -------------------------------------------------- */

const QUOTES = [
  {
    text: (
      <>
        I literally <HighlightSpan>need this</HighlightSpan> in my life. I
        spend hours every week just trying to keep everything straight.
      </>
    ),
    attribution: 'Mom of 3, Bay Area',
  },
  {
    text: (
      <>
        This is the app I didn&apos;t know I was waiting for. I keep telling my
        friends&mdash;<HighlightSpan>you need this</HighlightSpan>.
      </>
    ),
    attribution: 'Mom of 2, Austin',
  },
  {
    text: (
      <>
        I signed up the second I saw it. Finally someone understands that
        running a household is a{' '}
        <HighlightSpan>full-time job</HighlightSpan> on top of my full-time job.
      </>
    ),
    attribution: 'Working mom, Chicago',
  },
  {
    text: (
      <>
        My husband asked why I was so excited about an app.
        I said: <HighlightSpan>because it actually gets it</HighlightSpan>.
      </>
    ),
    attribution: 'Mom of 1, Denver',
  },
];

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

export const UserLoveSlide: React.FC = () => (
  <SlideBase>
    <WipOverlay>
      <WipBanner>Work in Progress</WipBanner>
    </WipOverlay>
    <SlideAccentGlow />
    <SlideContent>
      <SlideHeader title="User Love" />

      <TopSection>
        <BigNumber>[X,XXX]</BigNumber>
        <BigNumberLabel>Waitlist Sign-ups</BigNumberLabel>
        <SignupContext>Pre-launch, organic only — no paid acquisition</SignupContext>
      </TopSection>

      <QuoteGrid>
        {QUOTES.map((q, i) => (
          <QuoteCard key={i}>
            <QuoteText>{q.text}</QuoteText>
            <QuoteAttribution>&mdash; {q.attribution}</QuoteAttribution>
          </QuoteCard>
        ))}
      </QuoteGrid>

      <BottomNote>
        Placeholder — update sign-up count and quotes with real data before presenting.
      </BottomNote>
    </SlideContent>
  </SlideBase>
);
